import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Card,
    Button,
    Upload,
    Space,
    Typography,
    Alert,
    Spin,
    App,
    Divider,
    Progress,
    Tag,
    Descriptions,
    List,
    Input,
    InputNumber,
    Modal,
    Table,
    Select,
    Checkbox,
    Tooltip
} from 'antd';
import {
    UploadOutlined,
    SoundOutlined,
    CheckCircleOutlined,
    LoadingOutlined,
    AudioOutlined,
    StopOutlined,
    DeleteOutlined,
    HistoryOutlined,
    EyeOutlined,
    PlusOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { trIncludes } from '../../utils/trText';
import { useNavigate } from 'react-router-dom';
import WaveSurfer from 'wavesurfer.js';

const { Title, Text, Paragraph } = Typography;

const ToplantiPage = () => {
    const { darkMode } = useTheme();
    const navigate = useNavigate();
    const { message } = App.useApp();
    const [isProcessing, setIsProcessing] = useState(false);
    const [result, setResult] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isRecording, setIsRecording] = useState(false);
    const [recordedBlob, setRecordedBlob] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const [recordingTime, setRecordingTime] = useState(0);
    const recordingTimerRef = useRef(null);
    const wavesurferRef = useRef(null);
    const waveformContainerRef = useRef(null);
    const audioStreamRef = useRef(null);
    const [pipelineStatus, setPipelineStatus] = useState({
        upload: false,
        vad: false,
        transcription: false,
        diarization: false,
        correction: false,
        summary: false,
        saving: false
    });
    const [showFullSummary, setShowFullSummary] = useState(false);
    const [transcriptSegments, setTranscriptSegments] = useState([]);
    const [speakerMap, setSpeakerMap] = useState({});
    const [isUpdatingNames, setIsUpdatingNames] = useState(false);
    const recognitionRef = useRef(null);
    const [liveTranscript, setLiveTranscript] = useState('');
    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [usersList, setUsersList] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [sendEmails, setSendEmails] = useState(false);
    const [additionalParticipants, setAdditionalParticipants] = useState([]); 
    const [currentJobId, setCurrentJobId] = useState(null);
    const [jobProgress, setJobProgress] = useState('');
    const statusPollIntervalRef = useRef(null);
    const [processingModalVisible, setProcessingModalVisible] = useState(false);
    const [pendingJobId, setPendingJobId] = useState(null);

    const handleFileSelect = (file) => {
        const validTypes = ['audio/webm', 'audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/m4a', 'audio/x-m4a'];
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const validExtensions = ['webm', 'wav', 'mp3', 'm4a', 'opus'];
        
        if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
            message.error('Sadece ses dosyaları yüklenebilir (webm, wav, mp3, m4a)');
            return false;
        }
        
        setSelectedFile(file);
        setRecordedBlob(null); 
        message.success(`Dosya seçildi: ${file.name}`);
        return false; 
    };

    const showHistory = async () => {
        setHistoryModalVisible(true);
        setHistoryLoading(true);
        try {
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${API_BASE_URL}/speech2text/meetings`, {
                 headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setHistoryData(data);
            } else {
                message.error('Geçmiş kayıtlar yüklenemedi');
            }
        } catch (error) {
            console.error(error);
            message.error('Bir hata oluştu');
        } finally {
            setHistoryLoading(false);
        }
    };

    const fetchUsersList = async () => {
        setUsersLoading(true);
        try {
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${API_BASE_URL}/list/execute-query/kullanicilar`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ params: {} })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const users = Array.isArray(data) ? data : (data.data || data.users || []);
            const filteredUsers = users.filter(u => u.active !== 0 && u.ad_soyad);
            setUsersList(filteredUsers);
        } catch (err) {
            console.error('Kullanıcı listesi yükleme hatası:', err);
            message.error('Kullanıcı listesi yüklenemedi');
        } finally {
            setUsersLoading(false);
        }
    };

    const startRecording = async () => {
        try {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (SpeechRecognition) {
                const recognition = new SpeechRecognition();
                recognition.continuous = true;
                recognition.interimResults = true;
                recognition.lang = 'tr-TR';

                recognition.onresult = (event) => {
                    let interimTranscript = '';
                    for (let i = event.resultIndex; i < event.results.length; ++i) {
                        if (event.results[i].isFinal) {
                            setLiveTranscript(prev => prev + event.results[i][0].transcript + ' ');
                        } else {
                            interimTranscript += event.results[i][0].transcript;
                        }
                    }
                };

                recognition.onerror = (event) => {
                    console.error('Speech recognition error', event.error);
                };
                
                recognition.start();
                recognitionRef.current = recognition;
            } else {
                console.warn('Web Speech API not supported in this browser.');
            }

            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1
                } 
            });
            
            audioStreamRef.current = stream;
            
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : 'audio/webm';

            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: mimeType,
                audioBitsPerSecond: 128000
            });
            audioChunksRef.current = [];         
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            
            mediaRecorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: mimeType });
                setRecordedBlob(blob);
                setSelectedFile(null); 
                stream.getTracks().forEach(track => track.stop());
                if (recognitionRef.current) {
                    recognitionRef.current.stop();
                }
                if (wavesurferRef.current) {
                    wavesurferRef.current.destroy();
                    wavesurferRef.current = null;
                }
                message.success(`Kayıt tamamlandı! Süre: ${recordingTime} saniye`);
            };
            
            mediaRecorder.start(1000); 
            mediaRecorderRef.current = mediaRecorder;
            setIsRecording(true);
            setRecordingTime(0);
            setLiveTranscript('');
            
            recordingTimerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
            
            setTimeout(() => {
                if (waveformContainerRef.current) {
                    wavesurferRef.current = WaveSurfer.create({
                        container: waveformContainerRef.current,
                        waveColor: '#667eea',
                        progressColor: '#764ba2',
                        cursorColor: 'transparent',
                        barWidth: 3,
                        barRadius: 3,
                        barGap: 2,
                        height: 80,
                        normalize: true,
                        media: document.createElement('audio'),
                    });
                    
                    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                    const analyser = audioContext.createAnalyser();
                    const microphone = audioContext.createMediaStreamSource(stream);
                    microphone.connect(analyser);
                    analyser.fftSize = 256;
                    
                    const bufferLength = analyser.frequencyBinCount;
                    const dataArray = new Uint8Array(bufferLength);
                    
                    const canvas = document.createElement('canvas');
                    canvas.width = waveformContainerRef.current.clientWidth || 600;
                    canvas.height = 80;
                    canvas.style.width = '100%';
                    canvas.style.height = '80px';
                    canvas.style.borderRadius = '8px';
                    waveformContainerRef.current.innerHTML = '';
                    waveformContainerRef.current.appendChild(canvas);
                    const canvasCtx = canvas.getContext('2d');
                    
                    const draw = () => {
                        if (!isRecording && !audioStreamRef.current) return;
                        
                        requestAnimationFrame(draw);
                        analyser.getByteFrequencyData(dataArray);
                        
                        const gradient = canvasCtx.createLinearGradient(0, 0, canvas.width, 0);
                        gradient.addColorStop(0, '#667eea');
                        gradient.addColorStop(0.5, '#764ba2');
                        gradient.addColorStop(1, '#f093fb');
                        
                        canvasCtx.fillStyle = darkMode ? 'rgba(40, 40, 40, 0.6)' : '#ffffff';
                        canvasCtx.fillRect(0, 0, canvas.width, canvas.height);
                        
                        const barWidth = (canvas.width / bufferLength) * 2.5;
                        let x = 0;
                        
                        for (let i = 0; i < bufferLength; i++) {
                            const barHeight = (dataArray[i] / 255) * canvas.height * 0.8;
                            
                            canvasCtx.fillStyle = gradient;
                            canvasCtx.fillRect(x, (canvas.height - barHeight) / 2, barWidth - 1, barHeight);
                            
                            x += barWidth;
                        }
                    };
                    
                    draw();                    
                    wavesurferRef.current = { audioContext, analyser, microphone };
                }
            }, 100);
            
            message.success('Kayıt başladı');
        } catch (error) {
            console.error('Mikrofon erişim hatası:', error);
            message.error('Mikrofon erişimi için izin gerekli: ' + error.message);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            if (wavesurferRef.current) {
                if (wavesurferRef.current.audioContext) {
                    wavesurferRef.current.microphone?.disconnect();
                    wavesurferRef.current.audioContext.close();
                }
                if (wavesurferRef.current.destroy) {
                    wavesurferRef.current.destroy();
                }
                wavesurferRef.current = null;
            }
            audioStreamRef.current = null;
        }
    };

    const cleanupOldPendingJobs = useCallback(() => {
        try {
            const pendingJobs = JSON.parse(localStorage.getItem('pendingAudioJobs') || '[]');
            const now = new Date();
            const maxAge = 24 * 60 * 60 * 1000; 
            
            const validJobs = pendingJobs.filter(job => {
                if (!job.startTime) return false;
                const jobTime = new Date(job.startTime);
                const age = now - jobTime;
                return age < maxAge;
            });
            
            if (validJobs.length !== pendingJobs.length) {
                localStorage.setItem('pendingAudioJobs', JSON.stringify(validJobs));
            }
        } catch (error) {
            console.error('Pending jobs cleanup hatası:', error);
        }
    }, []);

    useEffect(() => {
        cleanupOldPendingJobs();
        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
            if (statusPollIntervalRef.current) {
                clearInterval(statusPollIntervalRef.current);
            }
        };
    }, [cleanupOldPendingJobs]);

    // 03.11.2026: kayıt tarayıcıda tutulup sonda tek seferde yüklenir; kayıt sürerken sunucuya istek gitmediği için
    // uzun toplantıda oturum hareketsizlikten kapanır, yükleme 401 alırdı. Kayıt bir işlemdir: sürdükçe 5 dk'da bir
    // oturum uzatılır (backend services/oturum.py).
    useEffect(() => {
        if (!isRecording) return undefined;
        const uzat = setInterval(() => {
            fetch(`${API_BASE_URL}/login/uzat`, { method: 'POST' }).catch(() => {});
        }, 5 * 60 * 1000);
        return () => clearInterval(uzat);
    }, [isRecording]);

    const pollJobStatus = async (jobId) => {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        try {
            const response = await fetch(`${API_BASE_URL}/speech2text/process-audio/status/${jobId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    console.warn(`Job ${jobId} backend'de bulunamadı`);
                    if (statusPollIntervalRef.current) {
                        clearInterval(statusPollIntervalRef.current);
                        statusPollIntervalRef.current = null;
                    }
                    const pendingJobs = JSON.parse(localStorage.getItem('pendingAudioJobs') || '[]');
                    const updatedJobs = pendingJobs.filter(j => j.jobId !== jobId);
                    localStorage.setItem('pendingAudioJobs', JSON.stringify(updatedJobs));
                    setIsProcessing(false);
                    setCurrentJobId(null);
                    setJobProgress('');
                    message.warning('İşlem durumu kontrol edilemedi. Backend yeniden başlatılmış olabilir.');
                    return;
                }
                throw new Error('Status kontrolü başarısız');
            }

            const statusData = await response.json();
            setJobProgress(statusData.progress || '');

            const progress = statusData.progress?.toLowerCase() || '';
            if (progress.includes('vad') || progress.includes('sesli bölüm')) {
                setPipelineStatus(prev => ({ ...prev, vad: true }));
                setUploadProgress(40);
            }
            if (progress.includes('transkripsiyon') || progress.includes('whisper')) {
                setPipelineStatus(prev => ({ ...prev, transcription: true }));
                setUploadProgress(60);
            }
            if (progress.includes('diarization') || progress.includes('speaker') || progress.includes('konuşmacı')) {
                setPipelineStatus(prev => ({ ...prev, diarization: true }));
                setUploadProgress(70);
            }
            if (progress.includes('düzelt') || progress.includes('deepseek') || progress.includes('correction')) {
                setPipelineStatus(prev => ({ ...prev, correction: true }));
                setUploadProgress(85);
            }
            if (progress.includes('özet') || progress.includes('summary')) {
                setPipelineStatus(prev => ({ ...prev, summary: true }));
                setUploadProgress(95);
            }
            if (progress.includes('veritabanı') || progress.includes('kaydedil')) {
                setPipelineStatus(prev => ({ ...prev, saving: true }));
                setUploadProgress(98);
            }

            if (statusData.status === 'completed') {
                if (statusPollIntervalRef.current) {
                    clearInterval(statusPollIntervalRef.current);
                    statusPollIntervalRef.current = null;
                }

                const resultResponse = await fetch(`${API_BASE_URL}/speech2text/process-audio/result/${jobId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!resultResponse.ok) {
                    throw new Error('Sonuç alınamadı');
                }

                const data = await resultResponse.json();
                setUploadProgress(100);
                setPipelineStatus(prev => ({
                    ...prev,
                    upload: true,
                    vad: true,
                    transcription: true,
                    diarization: true,
                    correction: true,
                    summary: true,
                    saving: true
                }));

                if (data.speaker_stats) {
                    const initialMap = {};
                    Object.keys(data.speaker_stats).forEach(speakerId => {
                        initialMap[speakerId] = speakerId;
                    });
                    setSpeakerMap(initialMap);
                }
                setResult(data);
                fetchUsersList();
                
                if (data.corrected_transcript) {
                    const lines = data.corrected_transcript.split('\n').filter(line => line.trim() !== '');
                    const segments = [];
                    let currentSpeaker = null;

                    lines.forEach(line => {
                        const match = line.match(/(\[.*?\])?\s*(Konuşmacı \d+|Konuşmacı_\d+):\s*(.*)/);
                        if (match) {
                            currentSpeaker = match[2].trim();
                            if (match[3]) { 
                                segments.push({ speaker: currentSpeaker, text: match[3].trim() });
                            }
                        } else if (currentSpeaker && line.trim()) {
                            segments.push({ speaker: currentSpeaker, text: line.trim() });
                        }
                    });
                    setTranscriptSegments(segments);
                } else {
                    setTranscriptSegments(data.segments || []);
                }
                
                message.success('Toplantı transkripti ve özeti başarıyla oluşturuldu!');
                setIsProcessing(false);
                setCurrentJobId(null);
                setJobProgress('');
                const pendingJobs = JSON.parse(localStorage.getItem('pendingAudioJobs') || '[]');
                const updatedJobs = pendingJobs.filter(j => j.jobId !== jobId);
                localStorage.setItem('pendingAudioJobs', JSON.stringify(updatedJobs));

            } else if (statusData.status === 'error') {
                if (statusPollIntervalRef.current) {
                    clearInterval(statusPollIntervalRef.current);
                    statusPollIntervalRef.current = null;
                }
                setIsProcessing(false);
                setCurrentJobId(null);
                setJobProgress('');
                message.error(statusData.error || 'İşlem sırasında hata oluştu');
                const pendingJobs = JSON.parse(localStorage.getItem('pendingAudioJobs') || '[]');
                const updatedJobs = pendingJobs.filter(j => j.jobId !== jobId);
                localStorage.setItem('pendingAudioJobs', JSON.stringify(updatedJobs));
            }

        } catch (error) {
            console.error('Status polling hatası:', error);
            if (statusPollIntervalRef.current) {
                clearInterval(statusPollIntervalRef.current);
                statusPollIntervalRef.current = null;
            }
            setIsProcessing(false);
            setCurrentJobId(null);
            message.error('İşlem durumu kontrol edilemedi');
        }
    };

    const processAudio = async () => {
        if (!selectedFile && !recordedBlob) {
            message.error('Lütfen bir ses dosyası seçin veya kayıt yapın');
            return;
        }
        
        const fileToProcess = selectedFile || new File([recordedBlob], 'recording.webm', { 
            type: recordedBlob?.type || 'audio/webm' 
        });

        setIsProcessing(true);
        setUploadProgress(0);
        setResult(null);
        setJobProgress('İşlem başlatılıyor...');
        setPipelineStatus({
            upload: false,
            vad: false,
            transcription: false,
            diarization: false,
            correction: false,
            summary: false,
            saving: false
        });

        const token = localStorage.getItem('auth_token');
        if (!token) {
            message.error('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
            setIsProcessing(false);
            return;
        }

        try {
            setPipelineStatus(prev => ({ ...prev, upload: true }));
            setUploadProgress(10);

            const formData = new FormData();
            formData.append('file', fileToProcess);
            setUploadProgress(20);

            const response = await fetch(`${API_BASE_URL}/speech2text/process-audio`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            setUploadProgress(30);

            if (!response.ok) {
                const errorText = await response.text();
                let errorData;
                try {
                    errorData = JSON.parse(errorText);
                } catch {
                    throw new Error(errorText || 'İşlem başarısız');
                }
                throw new Error(errorData.detail || 'İşlem başarısız');
            }

            const jobData = await response.json();
            
            if (!jobData.job_id) {
                throw new Error('Job ID alınamadı');
            }

            const pendingJobs = JSON.parse(localStorage.getItem('pendingAudioJobs') || '[]');
            pendingJobs.push({
                jobId: jobData.job_id,
                filename: fileToProcess.name,
                startTime: new Date().toISOString()
            });
            localStorage.setItem('pendingAudioJobs', JSON.stringify(pendingJobs));

            setPendingJobId(jobData.job_id);
            setProcessingModalVisible(true);
            
            if (statusPollIntervalRef.current) {
                clearInterval(statusPollIntervalRef.current);
                statusPollIntervalRef.current = null;
            }
            
            setIsProcessing(false);
            setUploadProgress(0);

        } catch (error) {
            console.error('Pipeline hatası:', error);
            message.error(error.message || 'Ses dosyası işlenirken hata oluştu');
            
            if (statusPollIntervalRef.current) {
                clearInterval(statusPollIntervalRef.current);
                statusPollIntervalRef.current = null;
            }
            setIsProcessing(false);
            setCurrentJobId(null);
            setJobProgress('');
        }
    };

    const resetForm = () => {
        if (statusPollIntervalRef.current) {
            clearInterval(statusPollIntervalRef.current);
            statusPollIntervalRef.current = null;
        }
        
        setSelectedFile(null);
        setRecordedBlob(null);
        setResult(null);
        setUploadProgress(0);
        setRecordingTime(0);
        setShowFullSummary(false);
        setTranscriptSegments([]);
        setSpeakerMap({});
        setIsUpdatingNames(false);
        setAdditionalParticipants([]);
        setSelectedUserIds({});
        setCurrentJobId(null);
        setJobProgress('');
        setIsProcessing(false);
        setPipelineStatus({
            upload: false,
            vad: false,
            transcription: false,
            diarization: false,
            correction: false,
            summary: false,
            saving: false
        });
    };

    const [selectedUserIds, setSelectedUserIds] = useState({});

    const handleSpeakerNameChange = (originalName, userId) => {
        const selectedUser = usersList.find(u => u.id === userId || u.kullanici_no === userId);
        const newName = selectedUser ? selectedUser.ad_soyad : originalName;
        setSpeakerMap(prevMap => ({
            ...prevMap,
            [originalName]: newName,
        }));
        setSelectedUserIds(prev => ({
            ...prev,
            [originalName]: userId ? (selectedUser?.id || selectedUser?.kullanici_no || userId) : null
        }));
    };

    const handleAddParticipant = () => {
        setAdditionalParticipants(prev => [...prev, { id: Date.now(), userId: null }]);
    };

    const handleRemoveParticipant = (participantId) => {
        setAdditionalParticipants(prev => prev.filter(p => p.id !== participantId));
    };

    const handleParticipantChange = (participantId, userId) => {
        setAdditionalParticipants(prev => 
            prev.map(p => p.id === participantId ? { ...p, userId } : p)
        );
    };

    const handleUpdateSpeakerNames = async () => {
        if (!result || !result.filename) {
            message.error("Güncellenecek dosya bilgisi bulunamadı.");
            return;
        }

        setIsUpdatingNames(true);
        const token = localStorage.getItem('auth_token');

        try {
            const filenameBase = result.filename.split('.')[0];
            const allSelectedUserIds = [];
            Object.values(selectedUserIds).forEach(id => {
                if (id !== null && id !== undefined) {
                    allSelectedUserIds.push(id);
                }
            });
            
            additionalParticipants.forEach(participant => {
                if (participant.userId !== null && participant.userId !== undefined) {
                    allSelectedUserIds.push(participant.userId);
                }
            });
            const uniqueUserIds = [...new Set(allSelectedUserIds)];

            const response = await fetch(`${API_BASE_URL}/speech2text/update-names`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    filename_base: filenameBase,
                    speaker_map: speakerMap,
                    send_emails: sendEmails,
                    summary_content: sendEmails && result.summary ? result.summary : null,
                    corrected_transcript: sendEmails && result.corrected_transcript ? result.corrected_transcript : null,
                    selected_user_ids: sendEmails && uniqueUserIds.length > 0 ? uniqueUserIds : null
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || "İsimler güncellenirken bir hata oluştu.");
            }

            let successMsg = "Konuşmacı isimleri başarıyla güncellendi ve kaydedildi!";
            if (sendEmails && data.emails_count > 0) {
                successMsg += ` ${data.emails_count} kullanıcıya mail gönderildi.`;
            }
            message.success(successMsg);

            const updatedSegments = transcriptSegments.map(segment => {
                const newSpeaker = speakerMap[segment.speaker];
                return {
                    ...segment,
                    speaker: newSpeaker && newSpeaker !== segment.speaker ? newSpeaker : segment.speaker
                };
            });
            setTranscriptSegments(updatedSegments);

            const newSpeakerMap = {};
            updatedSegments.forEach(segment => {
                if (!newSpeakerMap[segment.speaker]) {
                    newSpeakerMap[segment.speaker] = segment.speaker;
                }
            });
            setSpeakerMap(newSpeakerMap);

        } catch (error) {
            message.error(error.message);
        } finally {
            setIsUpdatingNames(false);
        }
    };

    return (
        <div style={{ padding: '24px' }}>
            <Card
                style={{
                    maxWidth: 1200,
                    margin: '0 auto',
                    boxShadow: darkMode ? '0 4px 12px rgba(255,255,255,0.1)' : '0 4px 12px rgba(0,0,0,0.1)'
                }}
            >
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <Title level={3}>
                                <SoundOutlined style={{ marginRight: 8 }} />
                                Otomatik Toplantı Transkripti ve Özeti
                            </Title>
                            <Paragraph type="secondary">
                                Ses dosyanızı yükleyin, sistem otomatik olarak konuşmacıları ayırır,
                                transkript oluşturur ve özet çıkarır.
                            </Paragraph>
                        </div>
                        <Button 
                            icon={<HistoryOutlined />} 
                            onClick={showHistory}
                        >
                            Geçmiş Kayıtlar
                        </Button>
                    </div>
                    {!result && (
                        <>
                            <Card type="inner" title="1. Ses Kaynağını Seçin">
                                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                    <Space size="middle" wrap>
                                        <Upload
                                            beforeUpload={handleFileSelect}
                                            maxCount={1}
                                            accept=".webm,.wav,.mp3,.m4a,audio/*"
                                            showUploadList={false}
                                            onRemove={() => setSelectedFile(null)}
                                            disabled={isProcessing || isRecording}
                                        >
                                            <Button
                                                icon={<UploadOutlined />}
                                                size="large"
                                                disabled={isProcessing || isRecording || recordedBlob}
                                            >
                                                Dosya Yükle
                                            </Button>
                                        </Upload>

                                        {!isRecording && !recordedBlob && (
                                            <Button
                                                icon={<AudioOutlined />}
                                                size="large"
                                                onClick={startRecording}
                                                disabled={isProcessing || selectedFile}
                                                style={{
                                                    backgroundColor: '#FF6D1F',
                                                    borderColor: '#FF6D1F',
                                                    color: 'white'
                                                }}
                                            >
                                                Canlı Kayıt Başlat
                                            </Button>
                                        )}

                                        {isRecording && (
                                            <Button
                                                icon={<StopOutlined />}
                                                size="large"
                                                onClick={stopRecording}
                                                danger
                                            >
                                                ⏹️ Kaydı Durdur ({recordingTime}s)
                                            </Button>
                                        )}
                                    </Space>

                                    {selectedFile && (
                                        <Alert
                                            message={`📁 Seçili dosya: ${selectedFile.name}`}
                                            description={`Boyut: ${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`}
                                            type="success"
                                            showIcon
                                            action={
                                                <Button 
                                                    icon={<DeleteOutlined />} 
                                                    type="text" 
                                                    danger 
                                                    onClick={() => {
                                                                setSelectedFile(null);
                                                                setIsProcessing(false);
                                                            }
                                                    } 
                                                />
                                            }
                                        />
                                    )}

                                    {recordedBlob && !isRecording && (
                                        <Alert
                                            message="Kayıt tamamlandı!"
                                            description={`Süre: ${recordingTime} saniye | Boyut: ${(recordedBlob.size / 1024 / 1024).toFixed(2)} MB`}
                                            type="success"
                                            showIcon
                                            action={
                                                <Button 
                                                    icon={<DeleteOutlined />} 
                                                    type="text" 
                                                    danger 
                                                    onClick={() => setRecordedBlob(null)} 
                                                />
                                            }
                                        />
                                    )}

                                    {isRecording && (
                                        <>
                                            <div style={{
                                                background: darkMode ? 'rgba(30, 30, 30, 0.8)' : '#f5f5f5',
                                                border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e0e0e0',
                                                borderRadius: 12,
                                                padding: 20,
                                                marginBottom: 16
                                            }}>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'space-between',
                                                    marginBottom: 16
                                                }}>
                                                    <Space>
                                                        <div style={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            background: '#ef4444',
                                                            animation: 'pulse 1.5s ease-in-out infinite',
                                                            boxShadow: '0 0 8px rgba(239, 68, 68, 0.6)'
                                                        }} />
                                                        <Text style={{ 
                                                            color: darkMode ? '#fff' : '#333', 
                                                            fontWeight: 500,
                                                            fontSize: 14
                                                        }}>
                                                            Kayıt Devam Ediyor
                                                        </Text>
                                                    </Space>
                                                    <Tag 
                                                        style={{ 
                                                            margin: 0, 
                                                            fontSize: 13, 
                                                            padding: '4px 10px',
                                                            background: darkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                                                            border: darkMode ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(239, 68, 68, 0.2)',
                                                            color: darkMode ? '#ff7875' : '#cf1322',
                                                            borderRadius: 6
                                                        }}
                                                    >
                                                        {Math.floor(recordingTime / 60).toString().padStart(2, '0')}:
                                                        {(recordingTime % 60).toString().padStart(2, '0')}
                                                    </Tag>
                                                </div>
                                                
                                                {/* Ses Spektrumu */}
                                                <div 
                                                    ref={waveformContainerRef}
                                                    style={{
                                                        width: '100%',
                                                        height: 60,
                                                        borderRadius: 6,
                                                        overflow: 'hidden',
                                                        background: darkMode ? 'rgba(50, 50, 50, 0.4)' : '#ffffff',
                                                        border: darkMode ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid #e8e8e8'
                                                    }}
                                                />
                                                
                                                <style>{`
                                                    @keyframes pulse {
                                                        0%, 100% { opacity: 1; transform: scale(1); }
                                                        50% { opacity: 0.5; transform: scale(1.1); }
                                                    }
                                                `}</style>
                                            </div>
                                            
                                            {liveTranscript && (
                                                <Card 
                                                    type="inner" 
                                                    title={
                                                        <Space>
                                                            <AudioOutlined style={{ color: darkMode ? '#52c41a' : '#52c41a' }} />
                                                            <span style={{ color: darkMode ? 'var(--dark-text)' : '#333' }}>Canlı Transcript</span>
                                                        </Space>
                                                    }
                                                    style={{ 
                                                        background: darkMode ? 'var(--dark-bg-secondary)' : '#fafbfc',
                                                        border: darkMode ? '1px solid var(--dark-border)' : '1px solid #e8e8e8'
                                                    }}
                                                >
                                                    <Paragraph style={{ 
                                                        marginBottom: 0,
                                                        color: darkMode ? 'var(--dark-text)' : '#333'
                                                    }}>
                                                        {liveTranscript}
                                                    </Paragraph>
                                                </Card>
                                            )}
                                        </>
                                    )}
                                </Space>
                            </Card>

                            <Card type="inner" title="2. İşlemeyi Başlat">
                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={isProcessing ? <LoadingOutlined /> : <SoundOutlined />}
                                        onClick={processAudio}
                                        disabled={(!selectedFile && !recordedBlob) || isProcessing || isRecording}
                                        loading={isProcessing}
                                        block
                                        style={{
                                            height: 60,
                                            fontSize: 16,
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {isProcessing ? 'İşleniyor...' : 'Otomatik İşlemeyi Başlat'}
                                    </Button>
                                    
                                    {isProcessing && (
                                        <>
                                            <Progress 
                                                percent={uploadProgress} 
                                                status={uploadProgress === 100 ? 'success' : 'active'}
                                                strokeColor={{
                                                    '0%': '#108ee9',
                                                    '100%': '#87d068',
                                                }}
                                            />
                                            {jobProgress && (
                                                <Alert
                                                    message={jobProgress}
                                                    type="info"
                                                    showIcon
                                                    style={{ marginTop: 8 }}
                                                />
                                            )}
                                            <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 16 }}>
                                                <Text type="secondary" strong>İşlem Adımları:</Text>
                                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                    <Space>
                                                        {pipelineStatus.upload ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <LoadingOutlined />}
                                                        <Text type={pipelineStatus.upload ? 'success' : 'secondary'}>Dosya Yükleme</Text>
                                                    </Space>
                                                    <Space>
                                                        {pipelineStatus.vad ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <LoadingOutlined />}
                                                        <Text type={pipelineStatus.vad ? 'success' : 'secondary'}>Sesli Bölüm Tespiti (VAD)</Text>
                                                    </Space>
                                                    <Space>
                                                        {pipelineStatus.transcription ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <LoadingOutlined />}
                                                        <Text type={pipelineStatus.transcription ? 'success' : 'secondary'}>Transkripsiyon (Whisper)</Text>
                                                    </Space>
                                                    <Space>
                                                        {pipelineStatus.diarization ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <LoadingOutlined />}
                                                        <Text type={pipelineStatus.diarization ? 'success' : 'secondary'}>Konuşmacı Ayrımı (Diarization)</Text>
                                                    </Space>
                                                    <Space>
                                                        {pipelineStatus.correction ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <LoadingOutlined />}
                                                        <Text type={pipelineStatus.correction ? 'success' : 'secondary'}>Metin Düzeltme (DeepSeek)</Text>
                                                    </Space>
                                                    <Space>
                                                        {pipelineStatus.summary ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <LoadingOutlined />}
                                                        <Text type={pipelineStatus.summary ? 'success' : 'secondary'}>Özet Oluşturma</Text>
                                                    </Space>
                                                    <Space>
                                                        {pipelineStatus.saving ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <LoadingOutlined />}
                                                        <Text type={pipelineStatus.saving ? 'success' : 'secondary'}>Veritabanına Kaydetme</Text>
                                                    </Space>
                                                </Space>
                                            </Space>
                                        </>
                                    )}
                                </Space>
                            </Card>
                        </>
                    )}

                    {result && (
                        <>
                            <Card
                                type="inner"
                                title={
                                    <Space>
                                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
                                        <Text strong style={{ fontSize: 18 }}>İşlem Tamamlandı!</Text>
                                    </Space>
                                }
                                extra={
                                    <Button onClick={resetForm}>
                                        Yeni İşlem
                                    </Button>
                                }
                            >
                                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                    {/*Konuşmacıların neler konuştuğunun çıktısını göster ve konuşmacıları kendimiz atayabilelim */}
                                    <Descriptions bordered column={2}>
                                        <Descriptions.Item label="Tespit Edilen Konuşmacı">
                                            <Tag color="purple">{result.speakers_detected} kişi</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Konuşmacı İstatistikleri">
                                            {result.speaker_stats && Object.entries(result.speaker_stats).map(([id, count]) => (
                                                <Tag key={id} color="cyan">
                                                    {speakerMap[id] || id}: {count} segment
                                                </Tag>
                                            ))}
                                        </Descriptions.Item>
                                    </Descriptions>

                                    <Divider>Konuşmacıları Adlandır</Divider>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {Object.keys(speakerMap).map(originalName => (
                                            <Space key={originalName} style={{ width: '100%' }} align="center">
                                                <Text strong style={{ width: '150px', display: 'inline-block' }}>
                                                    {originalName}:
                                                </Text>
                                                <Select
                                                    style={{ width: '300px' }}
                                                    placeholder="Konuşmacı seçin"
                                                    showSearch
                                                    filterOption={(input, option) =>
                                                        trIncludes(option?.label ?? '', input)
                                                    }
                                                    options={usersList.map(user => ({
                                                        value: user.id || user.kullanici_no,
                                                        label: user.ad_soyad,
                                                        key: user.id || user.kullanici_no
                                                    }))}
                                                    loading={usersLoading}
                                                    value={(() => {
                                                        const currentName = speakerMap[originalName];
                                                        if (currentName === originalName) return undefined;
                                                        const user = usersList.find(u => u.ad_soyad === currentName);
                                                        return user ? (user.id || user.kullanici_no) : undefined;
                                                    })()}
                                                    onChange={(userId) => handleSpeakerNameChange(originalName, userId)}
                                                    allowClear
                                                />
                                            </Space>
                                        ))}
                                        
                                        <Divider orientation="left" style={{ marginTop: 24 }}>
                                            <Text strong>Toplantı Katılımcıları</Text>
                                        </Divider>
                                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                            {additionalParticipants.map(participant => (
                                                <Space key={participant.id} style={{ width: '100%' }} align="center">
                                                    <Select
                                                        style={{ width: '300px' }}
                                                        placeholder="Katılımcı seçin"
                                                        showSearch
                                                        filterOption={(input, option) =>
                                                            trIncludes(option?.label ?? '', input)
                                                        }
                                                        options={usersList.map(user => ({
                                                            value: user.id || user.kullanici_no,
                                                            label: user.ad_soyad,
                                                            key: user.id || user.kullanici_no
                                                        }))}
                                                        loading={usersLoading}
                                                        value={participant.userId}
                                                        onChange={(userId) => handleParticipantChange(participant.id, userId)}
                                                        allowClear
                                                    />
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<CloseOutlined />}
                                                        onClick={() => handleRemoveParticipant(participant.id)}
                                                    />
                                                </Space>
                                            ))}
                                            <Button
                                                type="dashed"
                                                icon={<PlusOutlined />}
                                                onClick={handleAddParticipant}
                                                style={{ width: '300px' }}
                                            >
                                                Katılımcı Ekle
                                            </Button>
                                        </Space>
                                        
                                        <Checkbox
                                            checked={sendEmails}
                                            onChange={(e) => setSendEmails(e.target.checked)}
                                            style={{ marginTop: 16 }}
                                        >
                                            Seçilen tüm katılımcılara toplantı özetini mail ile gönder
                                        </Checkbox>
                                        <Button
                                            type="primary"
                                            onClick={handleUpdateSpeakerNames}
                                            loading={isUpdatingNames}
                                            disabled={isUpdatingNames}
                                            style={{ marginTop: 10 }}
                                        >
                                            İsimleri Kaydet {sendEmails && '(Mail Gönderilecek)'}
                                        </Button>
                                    </Space>

                                    <Divider>Özet</Divider>
                                    <Card type="inner">
                                        <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                                            {(showFullSummary || result.summary.length <= 500)
                                                ? result.summary
                                                : `${result.summary.substring(0, 500)}...`}
                                        </Paragraph>
                                        {result.summary.length > 500 && !showFullSummary && (
                                            <Button type="link" onClick={() => setShowFullSummary(true)} style={{ padding: 0 }}>
                                                Daha fazlasını gör
                                            </Button>
                                        )}
                                    </Card>

                                    <Divider>Konuşma Dökümü</Divider>
                                    <List
                                        className="transcript-list"
                                        itemLayout="horizontal"
                                        dataSource={transcriptSegments}
                                        renderItem={(item, index) => (
                                            <List.Item key={index}>
                                                <List.Item.Meta
                                                    title={
                                                        <Text strong>
                                                            {speakerMap[item.speaker] || item.speaker}:
                                                        </Text>
                                                    }
                                                    description={item.text}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </Space>
                            </Card>
                        </>
                    )}
                </Space>
            </Card>

            <Modal
                title="Geçmiş Toplantı Kayıtları"
                open={historyModalVisible}
                onCancel={() => setHistoryModalVisible(false)}
                footer={null}
                width={800}
                wrapClassName={darkMode ? 'dark-modal' : ''}
            >
                <Table
                    className={darkMode ? 'dark-table' : ''}
                    dataSource={historyData}
                    loading={historyLoading}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    columns={[
                        { title: 'Tarih', dataIndex: 'date', render: (text) => new Date(text).toLocaleString('tr-TR') },
                        { title: 'Dosya', dataIndex: 'filename' },
                        {
                            title: 'İşlem',
                            key: 'action',
                            render: (_, record) => (
                                <Button 
                                    icon={<EyeOutlined />} 
                                    type="link" 
                                    onClick={() => {
                                        setHistoryModalVisible(false);
                                        navigate(`/toplanti_ozet/${record.id}`);
                                    }}
                                >
                                    Görüntüle
                                </Button>
                            )
                        }
                    ]}
                />
            </Modal>

            {/* İşlem Başlatıldı Modal */}
            <Modal
                title="İşlem Başlatıldı"
                open={processingModalVisible}
                onOk={() => {
                    setProcessingModalVisible(false);
                    navigate('/home');
                }}
                onCancel={() => {
                    setProcessingModalVisible(false);
                    navigate('/home');
                }}
                okText="Tamam"
                cancelButtonProps={{ style: { display: 'none' } }}
                closable={false}
            >
                <p>İşlem arka planda devam ediyor. İşlem bitince size haber verilecektir.</p>
                <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                    Job ID: {pendingJobId}
                </p>
            </Modal>
        </div>
    );
};

export default ToplantiPage;